import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { initVersionedSchema, MixedSchema } from '@principle-theorem/editor';
import {
  createInsertTemplateMenuButton,
  EditorPresetsService,
  PrincipleEditorModule,
} from '@principle-theorem/ng-interactions';
import {
  DialogPresets,
  NgDialogModule,
  NgFireMediaUploader,
  NG_SHARED_CONFIG,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IContact,
  IContactDetails,
  IContactFormData,
  IPatient,
  IPractice,
  IReferral,
  IStaffer,
  isContact,
} from '@principle-theorem/principle-core/interfaces';
import { Referral } from '@principle-theorem/principle-core';
import {
  Firestore,
  filterUndefined,
  INamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import {
  ContactFormDialogComponent,
  InformationBoxComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { createInsertPatientDocumentMenuButton } from '@principle-theorem/ng-patient';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Storage } from '@angular/fire/storage';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildReferralTemplatesWithContext } from '../../helpers/build-referral-templates-with-context';

export interface IReferralComposeEmailDialogData {
  referral: WithRef<IReferral>;
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  currentUser: INamedDocument<IStaffer> | INamedDocument<IContact>;
  recipientEmail?: string;
  recipientName?: string;
  defaultSubject?: string;
  defaultContent?: MixedSchema;
}

export interface IReferralComposeEmailDialogResult {
  subject: string;
  content: MixedSchema;
}

interface IReferralComposeEmailForm {
  subject: string;
  content: MixedSchema;
}

@Component({
  selector: 'pr-referral-compose-email-dialog',
  imports: [
    NgDialogModule,
    MatFormFieldModule,
    PrincipleEditorModule,
    InformationBoxComponent,
  ],
  templateUrl: './referral-compose-email-dialog.component.html',
  styleUrl: './referral-compose-email-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralComposeEmailDialogComponent {
  private _editorPresets = inject(EditorPresetsService);
  private _organisation = inject(OrganisationService);
  private _storage = inject(Storage);
  private _sharedConfig = inject(NG_SHARED_CONFIG);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  dialogRef = inject(MatDialogRef<ReferralComposeEmailDialogComponent>);
  data = inject<IReferralComposeEmailDialogData>(MAT_DIALOG_DATA);
  menuItems: MenuButtonLoaderFn[];

  form = new TypedFormGroup<IReferralComposeEmailForm>({
    subject: new TypedFormControl<string>('', Validators.required),
    content: new TypedFormControl<MixedSchema>(initVersionedSchema()),
  });

  recipientEmail = signal(this.data.recipientEmail);

  hasRecipientEmail = computed(() => {
    const email = this.recipientEmail();
    return !!email && email.trim().length > 0;
  });

  dialogTitle = computed(() => {
    return this.data.referral.emailInvitationSent
      ? 'Notify Recipient of Changes'
      : 'Send Referral';
  });

  formValid = toSignal(
    this.form.statusChanges.pipe(map(() => this.form.valid)),
    { initialValue: this.form.valid }
  );

  canSend = computed(() => {
    return this.hasRecipientEmail() && this.formValid();
  });

  constructor() {
    this._initialiseForm();

    const uploader = new NgFireMediaUploader(
      this._storage,
      this._organisation.storagePath$.pipe(filterUndefined())
    );

    const menuCoordinator$ = new Subject<string>();
    this.menuItems = this._editorPresets.defaultMenuItems(uploader, [
      createInsertTemplateMenuButton({
        templates$: buildReferralTemplatesWithContext(
          this.data.referral,
          this.data.practice,
          this._sharedConfig.appUrl,
          this._organisation.brand$.pipe(filterUndefined())
        ),
        menuCoordinator$,
      }),
      createInsertPatientDocumentMenuButton({
        patient: this.data.patient,
        menuCoordinator$,
      }),
    ]);
  }

  send(): void {
    if (!this.canSend()) {
      return;
    }

    const data = this.form.getRawValue();

    const result: IReferralComposeEmailDialogResult = {
      subject: data.subject ?? '',
      content: data.content ?? initVersionedSchema(),
    };

    this.dialogRef.close(result);
  }

  async editContact(): Promise<void> {
    const contactActor = Referral.getContact(this.data.referral);

    if (!contactActor) {
      return;
    }

    const contact = await Firestore.getDoc(contactActor.ref);

    if (!isContact(contact)) {
      return;
    }

    const dialogData: IContactFormData = {
      title: `Edit ${contact.name}`,
      submitLabel: 'Save',
      contact,
    };

    const result = await this._dialog
      .open<
        ContactFormDialogComponent,
        IContactFormData,
        Partial<IContactDetails>
      >(ContactFormDialogComponent, DialogPresets.medium({ data: dialogData }))
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    await Firestore.patchDoc(contact.ref, result);
    this._snackBar.open('Contact Updated');

    if (result.email) {
      this.recipientEmail.set(result.email);
    }
  }

  private _initialiseForm(): void {
    const patientName = this.data.patient.name;
    const practiceName = this.data.practice.name;

    const defaultSubject =
      this.data.defaultSubject ?? `Referral for ${patientName}`;
    this.form.controls.subject.setValue(defaultSubject);

    if (this.data.defaultContent) {
      this.form.controls.content.setValue(this.data.defaultContent);
    } else {
      const defaultContent = this._buildDefaultContent(
        patientName,
        practiceName
      );
      this.form.controls.content.setValue(defaultContent);
    }
  }

  private _buildDefaultContent(
    patientName: string,
    practiceName: string
  ): MixedSchema {
    const recipientName = this.data.recipientName ?? 'there';
    const isUpdate = this.data.referral.emailInvitationSent;

    if (isUpdate) {
      return initVersionedSchema(
        `Dear ${recipientName},\n\nThere have been updates to the referral for ${patientName}. Please click the link below to view the changes.`
      );
    }

    return initVersionedSchema(
      `Dear ${recipientName},\n\n${practiceName} has sent you a referral for ${patientName}. Please click the link below to view the referral details.`
    );
  }
}
